import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../../../components/ui/button';
import { ArrowLeft, Eye, FolderOpen, PencilLine, CalendarDays, ClipboardCheck, CheckCircle2, Circle, Lock, ChevronRight } from 'lucide-react';
import { DossierTile, ModulesJourTile, ParticipantsTile, LieuTile, ProgrammeTile, LienActionTile } from './JourneeTiles';
import { JourneeMenu, computeSaisi } from './JourneeMenu';
import { PresencesPage } from './PresencesPage';
import { DeroulePage, ModulesCandidatsPage, ObservationsPage, EvaluationsPage } from './JourneePages';
import { SuiviTiles } from './SuiviTiles';
import { SaisieRapidePage } from './SaisieRapidePage';
import { Zap } from 'lucide-react';
import { CandidatsCadre } from './CandidatsCadre';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { ShieldCheck, FileSignature } from 'lucide-react';

// Le formateur quitte la fiche d'émargement sans l'avoir validée : on lui propose de valider
const EmargementNonValideDialog = ({ open, saving, onValider, onQuitter, onRester }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onRester()}>
    <DialogContent data-testid="emargement-non-valide-dialog">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSignature className="w-5 h-5 text-amber-600" /> Fiche d'émargement non validée</DialogTitle></DialogHeader>
      <p className="text-sm text-slate-600">Vous avez renseigné les présences mais la fiche d'émargement n'est pas validée. Souhaitez-vous la valider maintenant ?</p>
      <p className="text-xs text-slate-400">Sans validation, vos saisies sont conservées en brouillon mais les présences ne sont pas confirmées (synthèse, assiduité et alertes non générées).</p>
      <DialogFooter className="gap-2 sm:gap-0">
        <Button type="button" variant="ghost" onClick={onRester} disabled={saving} data-testid="emargement-rester">Rester sur la fiche</Button>
        <Button type="button" variant="outline" onClick={onQuitter} disabled={saving} data-testid="emargement-quitter-sans-valider">Quitter sans valider</Button>
        <Button type="button" className="bg-emerald-600 hover:bg-emerald-700" onClick={onValider} disabled={saving} data-testid="emargement-valider-quitter"><ShieldCheck className="w-4 h-4 mr-1" /> {saving ? 'Validation…' : 'Valider et quitter'}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const Cadre = ({ title, icon: Icon, action, children, testId, collapsible, open = true, onToggle, resume }) => (
  <section className="rounded-2xl border border-slate-200 bg-white/70 p-3 sm:p-4 space-y-3" data-testid={testId}>
    <div className="flex items-center justify-between gap-2">
      {collapsible ? (
        <button type="button" onClick={onToggle} className="flex-1 min-w-0 flex items-center gap-2 text-left group" aria-expanded={open} data-testid={`${testId}-toggle`}>
          <span className="w-7 h-7 rounded-lg bg-slate-100 group-hover:bg-indigo-50 flex items-center justify-center shrink-0 transition-colors"><ChevronRight className={`w-4 h-4 text-slate-500 group-hover:text-indigo-600 transition-transform ${open ? 'rotate-90' : ''}`} /></span>
          <span className="min-w-0">
            <span className="text-xs uppercase tracking-wide font-semibold text-slate-500 flex items-center gap-2"><Icon className="w-4 h-4" /> {title}</span>
            {!open && resume && <span className="block text-xs text-slate-600 truncate" data-testid={`${testId}-resume`}>{resume}</span>}
          </span>
        </button>
      ) : (
        <h2 className="text-xs uppercase tracking-wide font-semibold text-slate-500 flex items-center gap-2"><Icon className="w-4 h-4" /> {title}</h2>
      )}
      {action}
    </div>
    {(!collapsible || open) && children}
  </section>
);

const EtatBadge = ({ ok, label, testId }) => (
  <span className={`inline-flex items-center gap-1 ${ok ? 'text-emerald-600' : 'text-slate-400'}`} data-testid={testId}>{ok ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />} {label}</span>
);

// Page « journée » du portail formateur : cadre AF (informations) + cadre saisie journalière
export const JourneeView = ({ journeeData, cahier, setCahier, modules, saving, onSave, onBack, hasEvaluation, setHasEvaluation, onRefresh, onOpenAction }) => {
  const { dossier, formation, candidats, can_edit, planning_day, module_obligatoire, formation_modules, evaluations_etat: ev = {} } = journeeData;
  const [page, setPage] = useState(null);
  const [afOuvert, setAfOuvert] = useState(() => localStorage.getItem('journee_af_ouvert') === '1');
  const toggleAf = () => setAfOuvert(v => { localStorage.setItem('journee_af_ouvert', v ? '0' : '1'); return !v; });
  const formationModulesList = formation_modules || [];
  const formationIds = new Set(formationModulesList.map(m => m.id));
  const otherModulesList = modules.filter(m => !formationIds.has(m.id));
  const allModules = [...formationModulesList, ...otherModulesList, ...(modules || [])];
  const saisi = computeSaisi(cahier, hasEvaluation);
  const presents = cahier.presences.filter(p => p.statut === 'present').length;
  const resume = {
    presences: cahier.presences.length ? `${presents} présent(s) · ${cahier.presences.filter(p => p.statut === 'retard').length} retard(s) · ${cahier.presences.filter(p => p.statut === 'absent').length} absent(s)` : null,
    modules_candidats: saisi.modules_candidats ? `${(cahier.dispatch_modules || []).length} affectation(s)` : null,
    observations: saisi.observations ? `${(cahier.observations_candidats || []).filter(o => (o.texte || '').trim()).length} observation(s)` : null,
    evaluations: (
      <span className="flex flex-wrap gap-x-2" data-testid="evaluations-etat">
        <EtatBadge ok={ev.notes_saisies} label="Notes saisies" testId="etat-notes" />
        <EtatBadge ok={ev.acquis_saisis} label="Acquis saisis" testId="etat-acquis" />
      </span>
    ),
  };
  const [pendingLeave, setPendingLeave] = useState(null); // action à exécuter après la décision sur la fiche d'émargement
  const emargementNonValide = page === 'presences' && can_edit && !cahier.presences_validees && cahier.presences.length > 0;
  const garder = (next) => () => { if (emargementNonValide) setPendingLeave(() => next); else next(); };
  const back = garder(() => setPage(null));
  const backPortail = garder(onBack);
  const leaveValider = async () => { const valide = { ...cahier, presences_validees: true }; setCahier(valide); await onSave(valide); const next = pendingLeave; setPendingLeave(null); next?.(); };
  const leaveSansValider = async () => { await onSave(cahier); const next = pendingLeave; setPendingLeave(null); next?.(); };
  useEffect(() => {
    if (!emargementNonValide) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [emargementNonValide]);
  const menuLocked = can_edit && !cahier.modules_confirmes;
  const common = { journeeData, cahier, setCahier, onBack: back, onSave, saving };
  const lienColors = {
    fil: { bg: 'bg-fuchsia-50', label: 'text-fuchsia-500', text: 'text-fuchsia-900' },
    bilan: { bg: 'bg-teal-50', label: 'text-teal-500', text: 'text-teal-900' },
  };

  return (
    <div className="max-w-7xl mx-auto p-3 sm:p-6 space-y-4" data-testid="journee-view">
      <div className="flex items-center gap-3">
        <Button type="button" variant="ghost" size="sm" onClick={backPortail} data-testid="btn-back-portail"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
        <div>
          <h1 className="text-lg font-bold text-slate-900 capitalize">{format(parseISO(planning_day.date), 'EEEE d MMMM yyyy', { locale: fr })}</h1>
          <p className="text-xs mt-0.5" data-testid="journee-formateur-affecte">
            {journeeData.formateur_affecte ? <span className="text-indigo-600 font-medium">{dossier.type_dossier === 'aft' ? 'Formateur(s) / testeur(s)' : 'Formateur'} : {journeeData.formateur_affecte}</span> : <span className="text-amber-600 font-medium italic">Formateur en attente d'affectation</span>}
            {journeeData.testeur_scope && <span className="ml-2 text-violet-700 font-medium" data-testid="journee-testeur-scope">· Vous examinez {candidats.length} candidat(s) sur cette journée</span>}
          </p>
        </div>
      </div>

      {!can_edit && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-center gap-2">
          <Eye className="w-4 h-4 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-700"><span className="font-medium">Mode consultation</span> — Cette journée est affectée à un autre formateur. Vous pouvez consulter les informations mais pas les modifier.</p>
        </div>
      )}

      {!page && (
        <Cadre title="Action de formation" icon={FolderOpen} testId="cadre-af" collapsible open={afOuvert} onToggle={toggleAf}
          resume={[dossier.code, dossier.intitule, planning_day.lieu || dossier.lieu_principal, `${candidats.length} participant(s)`].filter(Boolean).join(' · ')}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <DossierTile dossier={dossier} formation={formation} onOpenAction={onOpenAction} />
            <ParticipantsTile dossier={dossier} count={candidats.length} />
            <LieuTile lieu={planning_day.lieu || dossier.lieu_principal} />
            <ProgrammeTile dossier={dossier} formation={formation} />
            <LienActionTile label="Fil chronologique" desc="Journées de l'action" icon={CalendarDays} color={lienColors.fil} tab="timeline" dossierId={dossier.id} onOpenAction={onOpenAction} testId="tile-fil-chronologique" />
            <LienActionTile label="Bilan" desc="Bilans de formation" icon={ClipboardCheck} color={lienColors.bilan} tab="bilan" dossierId={dossier.id} onOpenAction={onOpenAction} testId="tile-bilan" />
            <SuiviTiles dossierId={dossier.id} canEdit={can_edit} />
          </div>
        </Cadre>
      )}

      {!page && (
        <Cadre title="Saisie journalière" icon={PencilLine} testId="cadre-saisie"
          action={can_edit && (
            <button type="button" onClick={() => setPage('rapide')} title="Saisie rapide : modules, présences, déroulé" className="inline-flex items-center gap-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold pl-2 pr-2.5 h-7 shadow-sm active:scale-95 transition-transform" data-testid="btn-saisie-rapide">
              <Zap className="w-3.5 h-3.5" /> Rapide
            </button>
          )}>
          <ModulesJourTile cahier={cahier} setCahier={setCahier} allModules={allModules} formationModulesList={formationModulesList} otherModulesList={otherModulesList} canEdit={can_edit} hasEvaluation={hasEvaluation} isModuleObligatoire={!!module_obligatoire} onSave={onSave} saving={saving} formation={formation} planningDay={planning_day} />
          {menuLocked && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="menu-locked-notice">
              <Lock className="w-4 h-4 text-amber-600 shrink-0" />
              <p className="text-xs text-amber-800"><span className="font-semibold">Étape obligatoire :</span> ajoutez et confirmez au moins un module de la journée pour débloquer la saisie (présences, dispatch, déroulé, évaluations, observations).</p>
            </div>
          )}
          <JourneeMenu saisi={saisi} onOpen={setPage} resume={resume} locked={menuLocked} />
        </Cadre>
      )}
      {!page && <CandidatsCadre dossierId={dossier.id} date={planning_day.date} titre={dossier.type_dossier === 'aft' ? 'Candidats délégués ce jour (AFT)' : "Candidats de l'action"} refreshKey={cahier.modules_confirmes ? 1 : 0} />}

      {page === 'rapide' && <SaisieRapidePage {...common} formationModulesList={formationModulesList} otherModulesList={otherModulesList} hasEvaluation={hasEvaluation} />}
      {page === 'presences' && <PresencesPage {...common} onRefresh={onRefresh} />}
      {page === 'modules_candidats' && <ModulesCandidatsPage {...common} allModules={allModules} formationModulesList={formationModulesList} otherModulesList={otherModulesList} />}
      {page === 'deroule' && <DeroulePage {...common} />}
      {page === 'evaluations' && <EvaluationsPage journeeData={journeeData} cahier={cahier} setHasEvaluation={setHasEvaluation} onBack={back} />}
      {page === 'observations' && <ObservationsPage {...common} onRefresh={onRefresh} />}
      <EmargementNonValideDialog open={!!pendingLeave} saving={saving} onValider={leaveValider} onQuitter={leaveSansValider} onRester={() => setPendingLeave(null)} />
    </div>
  );
};
